import { request } from '@core/api';
import { useAppDispatch } from '@core/redux/hooks';
import { photosState, updatePhotoEverywhere } from '@photos/components/PhotosInViewport/state';
import { OrderNumber } from '@projects/types/orderNumbers';
import { Scan } from '@scans/types/scan';
import { scanThunks } from '@scans/redux';
import { updateScanInScansState } from '@scans/components/Scans/state';
import { Photo } from '@photos/types';
import { organizationManagementApiV1 as omaApi } from '@deepup/apis';
import { useEffect, useState } from 'react';
import { useOrderNumbers } from '@elements/components/ElementDialog/useOrderNumbers';

type SaveSMAPayload = {
  orderNumbers: OrderNumber[] | null;
  entityId: string;
  entityType: 'scans' | 'photos';
};

type UseSaveSMAProps = {
  onSuccess?: (updatedEntity: Photo | Scan) => void;
  projectId: string;
};

export const useSaveSMA = ({ onSuccess, projectId }: UseSaveSMAProps) => {
  const dispatch = useAppDispatch();
  const saveSMA = async ({ orderNumbers, entityId, entityType }: SaveSMAPayload) => {
    const requestBody = {
      orderNumbers,
    };
    const updatedEntity = await request<Photo | Scan, typeof requestBody>('PATCH', {
      path: ['projects', projectId!, entityType, entityId],
      body: requestBody,
    });

    if (updatedEntity.id && entityType === 'scans') {
      await dispatch(
        scanThunks.getOne({
          path: ['scans', updatedEntity.id],
          params: { projection: 'scanDetail' },
        }),
      );

      updateScanInScansState(updatedEntity as Scan);
    }

    if (entityType === 'photos') {
      const existingPhoto =
        photosState.singles.find((p) => p.id === entityId) ||
        photosState.clusters.flat().find((p) => p.id === entityId);

      if (existingPhoto) {
        const photoToUpdate = {
          ...existingPhoto,
          orderNumbers: updatedEntity.orderNumbers,
        };

        updatePhotoEverywhere(photoToUpdate);
      }
    }

    onSuccess?.(updatedEntity);
  };
  return { saveSMA };
};

export const useGetSMAMappings = (): {
  mappings: Record<string, omaApi.NetworkNodeMapping[]>;
  getSmaLabel: (smaNumber: string) => string;
  isLoading: boolean;
} => {
  const { orderNumbers, isLoading } = useOrderNumbers();
  const [mappings, setMappings] = useState<Record<string, omaApi.NetworkNodeMapping[]>>({});

  useEffect(() => {
    if (!orderNumbers) return;

    const map: Record<string, omaApi.NetworkNodeMapping[]> = {};
    orderNumbers.forEach((sma) => {
      map[sma.orderNumber] = sma.networkNodeMappings ?? [];
    });
    setMappings(map);
  }, [orderNumbers]);

  const getSmaLabel = (smaNumber: string) => {
    const [mapping] = mappings[smaNumber] || [];
    if (mapping) {
      return `${mapping.name} - ${smaNumber}`;
    }
    return smaNumber;
  };

  return {
    mappings,
    getSmaLabel,
    isLoading,
  };
};
